import { tradeRecords, STARTING_BALANCE } from '@/mocks/tradingData';

// The opening balance now lives in the trade document (journalLedger.txt) as the
// `@STARTING_BALANCE` setting, so it is edited right alongside the trades rather
// than in code. It is re-exported here so every existing importer keeps working.
export { STARTING_BALANCE };

const shortLabel = (date: Date): string =>
  date.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });

const labelOf = (dateStr: string): string => shortLabel(new Date(dateStr));

const firstTradeDate = new Date(tradeRecords[0].date);
const openingDate = new Date(firstTradeDate);
openingDate.setDate(openingDate.getDate() - 1);

let runningBalance = STARTING_BALANCE;

// One point per recorded trade: each result is accumulated onto the running balance,
// so the curve is a direct reflection of the single trade ledger (no separate series).
export const accountCurve = [
  {
    time: openingDate.getTime(),
    month: shortLabel(openingDate),
    balance: STARTING_BALANCE,
  },
  ...tradeRecords.map((trade) => {
    runningBalance = Number((runningBalance + trade.profit).toFixed(2));
    return {
      time: new Date(trade.date).getTime(),
      month: labelOf(trade.date),
      balance: runningBalance,
    };
  }),
];

// The most recent ~30 days of the ledger, used by the one-month balance view.
const lastTime = new Date(tradeRecords[tradeRecords.length - 1].date).getTime();
const thirtyDays = 30 * 24 * 60 * 60 * 1000;
const windowStart = lastTime - thirtyDays;

export const recentAccountCurve = accountCurve.filter((point) => point.time >= windowStart);

const dayYearLabel = (time: number): string =>
  new Date(time)
    .toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
    .toUpperCase();

// Human-readable label for the one-month window, e.g. "AUG 24, 2026 → SEP 21, 2026".
export const recentRange = {
  from: dayYearLabel(recentAccountCurve[0].time),
  to: dayYearLabel(recentAccountCurve[recentAccountCurve.length - 1].time),
};

const monthYear = (dateStr: string): string =>
  new Date(dateStr)
    .toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    .toUpperCase();

// Human-readable recorded range, e.g. "JUN 2026 → SEP 2026".
export const accountRange = {
  from: monthYear(tradeRecords[0].date),
  to: monthYear(tradeRecords[tradeRecords.length - 1].date),
};